import * as React from "react";
import * as ReactDOM from "react-dom";
import * as Rest from "../../Generated/RestClient";
import {BotAppContinuousIntegration, BotAppCIProps, BotAppCIState} from "./BotAppContinuousIntegration";
import {DownloadLinkControl, TooltipControl, Borders, TooltipParagrah} from "../../../../Intercom.ReactUI/Views/index";
import isInFrame from "../../../../Intercom.ReactUI/Utils/InFrame"
import {BladeType} from "../Services/azurePortalLinks";
import openTab from "../Services/openTab"
import {needZipProxy, getBotZipBlobUrl} from "../Services/downloadBotSrc";

export interface BotCodeSetupProps extends React.Props<BotCodeSetup>  {
  scmType: () => JQueryPromise<string>;
  ciProps: BotAppCIProps;
  setCodeSetup(component: BotCodeSetup): void;
  setLoading(loading: boolean) : void;
  onScmTypeChange(): void;
}

export interface BotCodeSetupState {
  bot: Rest.BotService;
  showingTipId: string;
  tip: JSX.Element;
  zipBlobUrl?: string;
}

const debugCodeSetup = false;

let showingTipId: string = null;

const tip_abs_code_build_tab = 'tip_abs_code_build_tab';

export class BotCodeSetup extends React.Component<BotCodeSetupProps, BotCodeSetupState> {
  private scmType: string = null;
  private azureBladUrl: string = null;
  private tipControl: TooltipControl;

  constructor(props: BotCodeSetupProps) {
    super(props);
    this.state = { bot : null, showingTipId: null, tip: null };
  }

  private get scmSiteUrl() : string {
    return this.state.bot ? `https://${this.state.bot.ProvisionInfo.SiteName}.scm.azurewebsites.net`: null;
  }

  private renderDownloadLink(): JSX.Element {
    if (!this.state.bot) {
      return null;
    }

    const fileName = `${this.state.bot.Id}.zip`;
    const botBlobUrl = needZipProxy() ? getBotZipBlobUrl(this.state.bot.Id, null) : (this.props.ciProps.blobUrl || this.state.zipBlobUrl);
    return <DownloadLinkControl
        asButton={true}
        fileName={fileName}
        downloadBlob={this.props.ciProps.downloadZip}
        setBlobUrl={blobUrl => {
          this.props.ciProps.onZipDownloaded(blobUrl);
          this.setState({zipBlobUrl: blobUrl});
          }}
        blobUrl={botBlobUrl}
        pendingDownload={this.props.ciProps.pendingDownload ? 'Generating zip file' : null}/>;
  }

  private shownTip: JSX.Element;
  private toggleTip(tip: JSX.Element, target: HTMLElement) {
    if (this.tipControl) {
      this.tipControl.anchorTo($(target));
    }
    if (this.shownTip && this.shownTip.key === tip.key) {
      this.dismissTooltip();
    } else {
      this.setState({tip: tip, showingTipId: tip ? tip_abs_code_build_tab: null});
      window.addEventListener('click', this.dismissTooltip);
      this.shownTip = tip;
    }
  }

  private dismissTooltip = () => {
    this.setState({tip: null, showingTipId: null});
    window.removeEventListener('click', this.dismissTooltip);
    this.shownTip = null;
  }

  private renderAppServiceEditorLink() : JSX.Element {
    return (
      <section className="bot-card">
        <h3>Online code editor</h3>
        <TooltipParagrah 
          toggleTip={this.toggleTip.bind(this)}
          tip={<span>Use the browser-based Azure App Service Editor to make quick changes to your source code, and test them instantly. <a href="//aka.ms/bf-doc-ase" key="ase-tip" target="_blank">Learn more</a></span>} 
          >
          Make quick changes to your bot code and see changes instantly. 
        </TooltipParagrah>
        {this.scmSiteUrl ? 
          <button className="light-button"
            onClick={() => openTab(`${this.scmSiteUrl}/dev`)}>
            Open online code editor
          </button>
          :null}
      </section>
    );
  }

  private renderDownloadCodeLink(): JSX.Element {
    return (
      <section className="bot-card">
        <h3>Download source code</h3>
        <TooltipParagrah
          toggleTip={this.toggleTip.bind(this)}
          tip={<span>Download a zip file that contains all source files, a Visual Studio solution file, and a configuration file that lets you publish from Visual Studio. <a href="//aka.ms/bf-doc-src-zip" key="src-zip-tip" target="_blank">Learn more</a></span>}
          >
          Download your source code and develop locally using
          your favorite IDE. You can publish your code back to the bot
          when ready. 
        </TooltipParagrah>
        {this.renderDownloadLink()}
      </section>
    );
  }

  private renderContinousIntegration() : JSX.Element {
    const text = this.scmType == 'None' ? 'Enable continuous deployment with your favorite source control provider.' : 'The code for this bot can be modified from the link below.';
    return (
      <section className="bot-card">
        <h3>Continuous deployment from source control</h3>
        <TooltipParagrah
          toggleTip={this.toggleTip.bind(this)}
          tip={<span>Continuous deployment lets you re-publish to Azure whenever you check a code change in to your source control service. <a href="//aka.ms/bf-doc-src-ci" key="ci-tip" target="_blank">Learn more</a></span>}>
          {text}
        </TooltipParagrah>
        <BotAppContinuousIntegration {...this.props.ciProps}
          toggleTip={this.toggleTip.bind(this)}
          showRepoUrl={this.scmType !== 'None'}
          onRepoSetup={() => { this.refreshScmType(); }}
          scmType={() => $.Deferred<string>().resolve(this.scmType)}/>
      </section>
    );
  }

  private renderOpenBotInAzure(): JSX.Element {
    return (
      <section className="bot-card grid-card channels">
        <div className="page-header">This bot is provisioned in Microsoft Azure</div>
        <h2 className="h-snap-M">Viewing and editing code in Microsoft Azure</h2>
        <p>To work with the code for this bot, open the bot in Microsoft Azure.</p>
        <button className="light-button open-bot-in-azure"
          disabled={this.azureBladUrl === null}
          onClick={() => {openTab(this.azureBladUrl)}}
          >Open in Microsoft Azure</button>
      </section>
    )
  }

  public componentDidMount() {
    if (!this.scmType) {
      this.props.setLoading(true);
    }
    this.props.scmType().then(scmType => {
      this.scmType = scmType;
      this.forceUpdate();
    }).always(
      () => this.props.setLoading(false)
    );
    this.props.ciProps.bladeUrl(BladeType._none).then(url => {
      this.azureBladUrl = url;
      this.forceUpdate();
    });
  }

  private renderCodeSetup() : JSX.Element {
    if (!this.scmType)  {
      // we haven't resolved the scmType
      return null;
    }

    return (
      <div className="agent-page page-grid bot-code-setup">
        <div className="bot-editor">
          {
            this.scmType === 'None' 
            ? <h1>Choose how to work with your code</h1>
            : null
          }
          {this.scmType == 'None' ? this.renderAppServiceEditorLink(): null}
          {this.scmType == 'None' ? this.renderDownloadCodeLink(): null}
          {this.renderContinousIntegration()}
        </div>
        {TooltipControl.renderToolTipAsNeeded({
          tipId: tip_abs_code_build_tab,
          ref: x => this.tipControl = x,
          children: this.state.tip,
          className: this.state.tip ?  'modeless' : 'invisible',
          anchorBorder: Borders.Left
          })
        }        
      </div>
    );
  }

  public refreshScmType() {
    // wait for 5 seconds so that the scmType update to propergate
    _.delay(() => {
      this.scmType = null;
      this.componentDidMount();
      this.props.onScmTypeChange();
    }, 5000);
  }

  public render() :JSX.Element {
    return isInFrame() || debugCodeSetup ? this.renderCodeSetup() : this.renderOpenBotInAzure();
  }
}


// WEBPACK FOOTER //
// ./Client/v4/Views/BotCodeSetup.tsx